/**
 * GaugeChart — radial gauge for single-value KPIs with a target range.
 *
 * The sweep shows the value's position in [min, max]; threshold bands
 * tint the arc. Used for bath temperature, pour temperature, sand
 * moisture — any metric where the customer cares about "am I in band".
 */

import { useMemo } from "react";
import { cn } from "@/lib/ui/cn";
import { ChartSkeleton } from "../ChartSkeleton";
import { GAUGE_END, GAUGE_LABEL_SIZE, GAUGE_START, arcPath, gaugeLayout, polar } from "./gauge-arc";
import type { WidgetState } from "../types";

export interface GaugeBand {
  /** Inclusive start (in the same unit as min/max). */
  from: number;
  to: number;
  /** Visual tint. */
  kind: "ok" | "watch" | "warn" | "danger";
}

export interface GaugeChartProps {
  value: number;
  min: number;
  max: number;
  unit?: string;
  /** Optional banded zones — drawn behind the indicator. */
  bands?: GaugeBand[];
  label?: string;
  size?: number;
  state?: WidgetState;
  className?: string;
}

const BAND_COLOR: Record<GaugeBand["kind"], string> = {
  ok: "rgb(var(--color-success))",
  watch: "rgb(var(--color-info))",
  warn: "rgb(var(--color-warning))",
  danger: "rgb(var(--color-danger))",
};

export function GaugeChart({
  value,
  min,
  max,
  unit,
  bands = [],
  label,
  size = 180,
  state = { kind: "ok" },
  className,
}: GaugeChartProps) {
  const { cx, cy, r, height, labelY, minLabelX, maxLabelX } = gaugeLayout(size);

  if (state.kind === "loading") {
    return <ChartSkeleton shape="gauge" size={size} height={height} className={className} />;
  }

  const clamped = Math.max(min, Math.min(max, value));
  // Sweep over 240° (from 150° to 30°, going clockwise over the top), so
  // the open bottom 120° wedge gives space for the readout.
  const startAngle = GAUGE_START;
  const endAngle = GAUGE_END;
  const range = max - min;
  const valueAngle = startAngle + ((clamped - min) / range) * (endAngle - startAngle);

  const arc = (a0: number, a1: number, color: string, width: number) => (
    <path d={arcPath(cx, cy, r, a0, a1)} stroke={color} strokeWidth={width} strokeLinecap="round" fill="none" />
  );

  const bandSegments = useMemo(() => {
    return bands.map((b, i) => {
      const a0 =
        startAngle + (Math.max(min, b.from) - min) / range * (endAngle - startAngle);
      const a1 =
        startAngle + (Math.min(max, b.to) - min) / range * (endAngle - startAngle);
      return (
        <g key={i} opacity={0.35}>
          {arc(a0, a1, BAND_COLOR[b.kind], 14)}
        </g>
      );
    });
  }, [bands, min, max, range]);

  const indicator = polar(cx, cy, r, valueAngle);
  const inSpec = bands.find((b) => clamped >= b.from && clamped <= b.to);
  const valueColor = inSpec ? BAND_COLOR[inSpec.kind] : "rgb(var(--color-on-surface))";

  return (
    <div className={cn("flex max-h-full max-w-full flex-col items-center", className)}>
      {/* A tile smaller than the gauge shrinks the picture through its viewBox: ChartCard would clip it. */}
      <svg width={size} height={height} viewBox={`0 0 ${size} ${height}`} className="min-h-0 max-w-full">
        {/* Base ring */}
        {arc(startAngle, endAngle, "rgb(var(--color-border))", 8)}
        {/* Bands */}
        {bandSegments}
        {/* Value sweep */}
        {arc(startAngle, valueAngle, valueColor, 6)}
        {/* Needle dot */}
        <circle cx={indicator.x} cy={indicator.y} r={6} fill={valueColor} stroke="rgb(var(--color-surface))" strokeWidth={2} />
        {/* Center readout */}
        <text
          x={cx}
          y={cy + 6}
          textAnchor="middle"
          className="fill-on-surface"
          fontSize={size * 0.18}
          fontWeight={600}
        >
          {formatValue(value)}
        </text>
        {unit && (
          <text x={cx} y={cy + 26} textAnchor="middle" className="fill-on-surface-muted" fontSize={10}>
            {unit}
          </text>
        )}
        {/* Min/max ticks */}
        <text x={minLabelX} y={labelY} textAnchor="start" fontSize={GAUGE_LABEL_SIZE} className="fill-on-surface-muted">
          {min}
        </text>
        <text x={maxLabelX} y={labelY} textAnchor="end" fontSize={GAUGE_LABEL_SIZE} className="fill-on-surface-muted">
          {max}
        </text>
      </svg>
      {label && <p className="mt-1 text-center text-xs text-on-surface-muted">{label}</p>}
    </div>
  );
}

function formatValue(n: number): string {
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (Math.abs(n) >= 100) return Math.round(n).toString();
  return n.toFixed(1);
}
